import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import PasswordInput from '../components/PasswordInput';
import { getPasswordPolicyError, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function AcceptInvitation() {
  const [invitationToken] = useState(() => {
    const fragment = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : '';
    return new URLSearchParams(fragment).get('token') || '';
  });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // The server never receives fragments. Remove the one-time token from the
    // browser's visible URL and history before the user can navigate away. A
    // legacy query token is never used and is removed as well.
    if (window.location.hash || new URLSearchParams(window.location.search).has('token')) {
      const safeUrl = new URL(window.location.href);
      safeUrl.hash = '';
      safeUrl.searchParams.delete('token');
      window.history.replaceState(
        null,
        document.title,
        `${safeUrl.pathname}${safeUrl.search}`,
      );
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!invitationToken) {
      setError('This invitation link is missing or invalid.');
      return;
    }
    const policyError = getPasswordPolicyError(password);
    if (policyError) {
      setPasswordPolicyError(policyError);
      setError(policyError);
      return;
    }
    if (password !== confirmation) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/auth/accept-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: invitationToken, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to accept this invitation');
      setComplete(true);
      setPassword('');
      setConfirmation('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page">
      <div className="login-section">
        <div className="login-container">
          <div className="login-card">
            <h1 className="login-title">Set Up Your Account</h1>
            {complete ? (
              <>
                <p className="login-subtitle">Your password is set. Sign in to receive an email code and finish setting up your account.</p>
                <Link className="login-button login-button-link" to="/login">Continue to Sign In</Link>
              </>
            ) : (
              <>
                <p className="login-subtitle">Choose a private password to accept your invitation.</p>
                {error && <div className="login-error">{error}</div>}
                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="invitation-password">Password</label>
                    <PasswordInput
                      id="invitation-password"
                      label="password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                      value={password}
                      onChange={(event) => {
                        const nextPassword = event.target.value;
                        setPassword(nextPassword);
                        const nextPolicyError = getPasswordPolicyError(nextPassword);
                        setPasswordPolicyError(nextPolicyError);
                        if (!nextPolicyError) setError('');
                      }}
                    />
                    <p className="password-policy-hint">{PASSWORD_POLICY_MESSAGE}</p>
                    {passwordPolicyError && <p className="password-policy-error" role="alert">{passwordPolicyError}</p>}
                  </div>
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="invitation-confirmation">Confirm Password</label>
                    <PasswordInput id="invitation-confirmation" label="confirm password" autoComplete="new-password" minLength={8} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
                  </div>
                  <button className="login-button" type="submit" disabled={loading}>{loading ? 'Setting Password…' : 'Set Password'}</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitation;
